import React, {useState, useEffect} from 'react'
import {Box} from '@mui/material'
import {
  ActionsBox,
  ActionsFlexBox,
  AutoplayBox,
  BottomBox,
  CheckBox,
  CongratulationsText,
  ContentBox,
  ContentLink,
  CourseTitle,
  ExamIcon,
  FlashcardIcon,
  IconBox,
  LinkButton,
  LinkButton2,
  NextButton,
  ProgressBox,
  QuizBreakIcon,
  StudyGuideIcon,
  StyledCheckIcon,
  StyledPlayArrowIcon,
  Title,
  UpNext,
  VideoEndedBox,
  VideoInternalBox,
  VideoProgress,
} from './styles'
import {Link as RouterLink, useNavigate} from 'react-router-dom'
import CircularWithValueLabel from '../../../components/CircularProgress'
import Cookies from 'universal-cookie'
import {StyledIcon} from '../../../components/CircularProgress/styles'
import {
  StudyGuide,
  StudyGuideAssets,
  StudyGuideStudyDays,
} from '../../../redux/studyGuides'
import {LinkBox} from '../../../styles/global'

import ReplayIcon from '../../../images/icons/replay-icon.svg'
import NextContentIcon from '../../../images/icons/next-content-icon.svg'

type Props = {
  videoId?: string
  studyGuide?: StudyGuide
  onChangeVideoEnded: (value: boolean) => void
  loadVideo: () => void
  onSaveProgress: (value: number) => void
  studyDay?: number
}

const View = ({
  videoId,
  studyGuide,
  onChangeVideoEnded,
  onSaveProgress,
  loadVideo,
  studyDay,
}: Props) => {
  const navigate = useNavigate()
  const cookies = new Cookies()

  const dayAssets = studyGuide?.assets?.find(a => a.studyDay === studyDay)
  const nextDayAssets = studyGuide?.assets?.find(
    a => a.studyDay === (studyDay ?? 0) + 1,
  )

  const getUnifiedAssets = (assets?: StudyGuideStudyDays) => {
    return assets
      ? [
          ...(Array.isArray(assets.video) ? assets.video : []),
          ...(Array.isArray(assets.quiz) ? assets.quiz : []),
          ...(Array.isArray(assets.flashcard_deck)
            ? assets.flashcard_deck
            : []),
          ...(Array.isArray(assets.lecture_slides)
            ? assets.lecture_slides
            : []),
          ...(Array.isArray(assets.practice_exam) ? assets.practice_exam : []),
          ...(Array.isArray(assets.break) ? assets.break : []),
        ]
      : []
  }

  const unifiedAssets = getUnifiedAssets(dayAssets)
  const nextDayUnifiedAssets = getUnifiedAssets(nextDayAssets)

  const currentVideo = dayAssets?.video.find(v => v.id === videoId)

  const videos = dayAssets?.video
  const currentVideoIndex = videos?.findIndex(v => v.id === videoId)

  const currentAssetIndex = unifiedAssets?.findIndex(
    v => v.id === videoId && v.assetType === 'video',
  )
  const nextAsset =
    currentAssetIndex > -1
      ? (unifiedAssets ?? [])[currentAssetIndex + 1]
      : undefined

  const [autoplay, setAutoplay] = useState(
    nextAsset && cookies.get('bks-autoplay')
      ? cookies.get('bks-autoplay')
      : false,
  )

  const getNextAssetUrl = (asset?: StudyGuideAssets) => {
    let type = ''

    if (asset) {
      switch (asset.assetType) {
        case 'video':
          type = 'videos'
          break
        case 'quiz':
          type = 'quizzes'
          break
        case 'flashcard_deck':
          type = 'flashcards'
          break
        case 'lecture_slides':
          type = 'slides'
          break
        case 'break':
          type = 'breaks'
          break
        case 'practice_exam':
          type = 'practice-exams'
          break
      }

      return type === 'slides'
        ? asset.downloadUrl ?? ''
        : `/study-guide/${studyGuide?.id}/${asset?.studyDay}/${type}/${asset?.id}`
    }

    return ''
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        autoplay &&
        getNextAssetUrl(nextAsset) &&
        nextAsset?.assetType === 'video'
      ) {
        goToNext(getNextAssetUrl(nextAsset))
      }

      clearTimeout(timer)
    }, 5500)

    return () => {
      clearTimeout(timer)
    }
  }, [autoplay])

  const handleChangeAutoplay = () => {
    const newValue = !autoplay

    setAutoplay(newValue)

    cookies.set('bks-autoplay', newValue, {
      path: '/',
    })
  }

  const goToNext = (url: string, reloadVideo?: boolean) => {
    if (
      nextAsset?.assetType === 'lecture_slides' &&
      !nextAsset.isComplete &&
      !reloadVideo
    ) {
      window.open(url)
      onSaveProgress(nextAsset.assetGlobalId)
    } else {
      onChangeVideoEnded(false)
      navigate(url, {
        replace: true,
      })
    }

    if (reloadVideo) {
      loadVideo()
    }
  }

  return (
    <VideoEndedBox>
      <VideoInternalBox>
        {nextAsset ? (
          <>
            <ProgressBox>
              {nextAsset.assetType !== 'video' ? (
                <LinkButton
                  onClick={() => goToNext(getNextAssetUrl(nextAsset))}
                >
                  {nextAsset.assetType === 'quiz' ? (
                    <QuizBreakIcon />
                  ) : (
                    <>
                      {nextAsset.assetType === 'flashcard_deck' ? (
                        <FlashcardIcon />
                      ) : (
                        <>
                          {nextAsset.assetType === 'practice_exam' ? (
                            <ExamIcon />
                          ) : (
                            <StudyGuideIcon />
                          )}
                        </>
                      )}
                    </>
                  )}
                </LinkButton>
              ) : (
                <LinkButton
                  onClick={() => goToNext(getNextAssetUrl(nextAsset))}
                >
                  {autoplay ? (
                    <CircularWithValueLabel />
                  ) : (
                    <NextButton>
                      <StyledIcon />
                    </NextButton>
                  )}
                </LinkButton>
              )}
            </ProgressBox>
            <Box>
              <CourseTitle>Study Day {dayAssets?.studyDay}</CourseTitle>
              <VideoProgress>{`${
                currentVideoIndex! + 1
              }/${videos?.length} videos completed`}</VideoProgress>
              <UpNext>
                Up Next:
                <br />
                <LinkButton2
                  onClick={() => goToNext(getNextAssetUrl(nextAsset))}
                >
                  {nextAsset.assetTitle}
                </LinkButton2>
              </UpNext>
            </Box>
          </>
        ) : (
          <ActionsFlexBox>
            <ActionsBox>
              <IconBox $hover={false} style={{backgroundColor: '#1377D4'}}>
                <StyledCheckIcon style={{width: '40px', height: '40px'}} />
              </IconBox>
              <ContentBox>
                <CongratulationsText>Congratulations</CongratulationsText>
                <Title>
                  Great Work! You’ve Completed Day {dayAssets?.studyDay} of Your
                  Study Plan!
                </Title>
              </ContentBox>
            </ActionsBox>
            {(dayAssets?.studyDay ?? 0) < (studyGuide?.timeWindow ?? 0) && (
              <ActionsBox>
                <IconBox
                  $hover={true}
                  onClick={() =>
                    goToNext(getNextAssetUrl(nextDayUnifiedAssets[0]))
                  }
                >
                  <StyledPlayArrowIcon />
                </IconBox>
                <ContentBox>
                  <CongratulationsText color={'#fff'}>
                    Up Next:
                  </CongratulationsText>
                  <LinkBox
                    component={RouterLink}
                    to={getNextAssetUrl(nextDayUnifiedAssets[0])}
                  >
                    <ContentLink>
                      Day {(dayAssets?.studyDay ?? 0) + 1}
                    </ContentLink>
                  </LinkBox>
                </ContentBox>
              </ActionsBox>
            )}
          </ActionsFlexBox>
        )}
      </VideoInternalBox>
      <BottomBox>
        {videos && nextAsset && nextAsset.assetType === 'video' && (
          <>
            <AutoplayBox onClick={handleChangeAutoplay}>
              Autoplay next{' '}
              {videos.length - (currentVideoIndex! + 1) === 1
                ? 'video'
                : `${videos.length - (currentVideoIndex! + 1)} videos`}
              <CheckBox $isActive={autoplay}>{autoplay && '✓'}</CheckBox>
            </AutoplayBox>
          </>
        )}
        <LinkButton2
          onClick={() => goToNext(getNextAssetUrl(currentVideo), true)}
        >
          Replay
          <img src={ReplayIcon} />
        </LinkButton2>
        {nextAsset &&
        ((nextAsset.assetType === 'lecture_slides' && nextAsset.isComplete) ||
          nextAsset.assetType !== 'lecture_slides') ? (
          <>
            <LinkButton2
              onClick={() =>
                nextAsset.assetType === 'lecture_slides' && nextAsset.isComplete
                  ? goToNext(getNextAssetUrl(nextDayUnifiedAssets[0]))
                  : goToNext(getNextAssetUrl(nextAsset))
              }
            >
              {nextAsset.assetType === 'lecture_slides' && nextAsset.isComplete
                ? `Move to Day ${Number(studyDay) + 1}`
                : 'Next'}
              <img src={NextContentIcon} />
            </LinkButton2>
          </>
        ) : null}
      </BottomBox>
    </VideoEndedBox>
  )
}

export default View
